import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, LayoutDashboard, ShoppingCart, Users, Package, CreditCard, Settings, Plus, Download, Clock, QrCode } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface NavbarProps {
  onToggle: () => void;
  onAction?: (action: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ onToggle, onAction }) => {
  const location = useLocation();
  const { theme } = useTheme();

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const pageInfo = {
    '/dashboard': { 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      description: 'Overview and analytics',
      actions: [],
      showLiveIndicator: false
    },
    '/orders': { 
      icon: ShoppingCart, 
      label: 'Orders',
      description: 'Track and manage orders',
      actions: [
        { id: 'new-order', label: 'New Order', icon: Plus, variant: 'primary' }
      ],
      showLiveIndicator: false
    },
    '/order-history': { 
      icon: Clock, 
      label: 'Order History',
      description: 'Historical order data',
      actions: [
        { id: 'export-orders', label: 'Export', icon: Download, variant: 'secondary' }
      ],
      showLiveIndicator: false
    },
    '/customers': { 
      icon: Users, 
      label: 'Customers',
      description: 'Customer management',
      actions: [
        { id: 'add-customer', label: 'Add Customer', icon: Plus, variant: 'primary' }
      ],
      showLiveIndicator: false
    },
    '/inventory': { 
      icon: Package, 
      label: 'Inventory',
      description: 'Stock and materials',
      actions: [],
      showLiveIndicator: false
    },
    '/payments': { 
      icon: CreditCard, 
      label: 'Payments',
      description: 'Payment tracking',
      actions: [
        { id: 'export-payments', label: 'Export', icon: Download, variant: 'secondary' }
      ],
      showLiveIndicator: true
    },
    '/features': { 
      icon: QrCode, 
      label: 'Features',
      description: 'QR codes and customer features',
      actions: [],
      showLiveIndicator: false
    },
    '/user-management': { 
      icon: Users, 
      label: 'User Management',
      description: 'Manage team members and roles',
      actions: [],
      showLiveIndicator: false
    },
    '/subscription': { 
      icon: CreditCard, 
      label: 'Subscription',
      description: 'Manage your subscription plan',
      actions: [],
      showLiveIndicator: false
    },
    '/settings': { 
      icon: Settings, 
      label: 'Settings',
      description: 'System configuration',
      actions: [],
      showLiveIndicator: false
    },
  };

  // Check if we're on an order details page
  const isOrderDetailsPage = location.pathname.startsWith('/orders/') && location.pathname !== '/orders';
  
  const currentPage = isOrderDetailsPage ? {
    icon: ShoppingCart,
    label: 'Order Details',
    description: 'Order information and status',
    actions: [],
    showLiveIndicator: false
  } : pageInfo[location.pathname as keyof typeof pageInfo] || { 
    icon: LayoutDashboard, 
    label: 'Dashboard',
    description: 'Overview and analytics',
    actions: [],
    showLiveIndicator: false
  };
  const Icon = currentPage.icon;

  const handleActionClick = (actionId: string) => {
    if (onAction) {
      onAction(actionId);
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 px-4 py-3 transition-colors duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Sidebar Toggle */}
          {!isDesktop && (
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}

          {/* Current Page Name */}
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{currentPage.label}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-none">{currentPage.description}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {(currentPage.actions.length > 0 || currentPage.showLiveIndicator) && (
          <div className="flex items-center space-x-3">
            {/* Live Indicator */}
            {currentPage.showLiveIndicator && (
              <div className="flex items-center space-x-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Live</span>
              </div>
            )}
            
            {/* Action Buttons */}
            {currentPage.actions.map((action) => {
              const ActionIcon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => handleActionClick(action.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    action.variant === 'primary'
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  <ActionIcon className="w-4 h-4" />
                  <span className="text-sm">{action.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 